import React, { useEffect, useState } from "react";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE } from "../../../ApiUrl";
import { useNavigate, useParams } from "react-router-dom";
import { FiSave, FiX, FiArrowLeft } from "react-icons/fi";
import "./AddIndustry.css";

function AddIndustry() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditMode);
  const [originalRecord, setOriginalRecord] = useState(null);

  const [formData, setFormData] = useState({
    code: "",
    label: "",
    is_active: true,
  });

  useEffect(() => {
    if (isEditMode) {
      fetchIndustry();
    }
  }, [id]);

  const fetchIndustry = async () => {
    setFetching(true);

    try {
      const res = await axios.get(`${API_BASE}/reference-values/${id}/`);
      const item = res.data?.data || res.data;

      setOriginalRecord(item);

      setFormData({
        code: item.code || "",
        label: item.label || "",
        is_active: item.is_active ?? true,
      });
    } catch (error) {
      console.error("Error fetching industry:", error);
      Swal.fire("Error", "Failed to load industry details", "error");
      navigate("/catalog/industries");
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleActive = () => {
    setFormData((prev) => ({
      ...prev,
      is_active: !prev.is_active,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.code.trim() || !formData.label.trim()) {
      Swal.fire(
        "Validation Error",
        "Code and Label are required",
        "warning"
      );
      return;
    }

    setLoading(true);

    try {
      if (isEditMode) {
        const payload = {
          option_set: 14,
          code: formData.code.trim(),
          label: formData.label.trim(),
          sort_order: originalRecord?.sort_order ?? 0,
          is_system: originalRecord?.is_system ?? false,
          is_active: formData.is_active,
          metadata: originalRecord?.metadata ?? {},
          parent: originalRecord?.parent ?? null,
          created_by: originalRecord?.created_by ?? 1,
        };

        await axios.put(`${API_BASE}/reference-values/${id}/`, payload);

        Swal.fire({
          icon: "success",
          title: "Updated!",
          text: "Industry has been updated.",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        const payload = {
          option_set: 14,
          code: formData.code.trim(),
          label: formData.label.trim(),
          sort_order: 0,
          is_system: false,
          is_active: formData.is_active,
          metadata: {},
          parent: null,
          created_by: 1,
        };

        await axios.post(`${API_BASE}/reference-values/`, payload);

        Swal.fire({
          icon: "success",
          title: "Added!",
          text: "Industry has been created.",
          timer: 2000,
          showConfirmButton: false,
        });
      }

      navigate("/catalog/industries");
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} industry:`,
        error
      );

      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          `Failed to ${isEditMode ? "update" : "create"} industry`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (fetching) {
    return (
      <div className="app-layout">
        <Sidebar />

        <div className="main-content">
          <Header />

          <div className="industry-form-page">
            <div className="industry-form-loader">
              <div className="industry-form-spinner"></div>
              <p>Loading industry...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="industry-form-page">
          <div className="industry-form-top">
            <button
              type="button"
              className="industry-back-btn"
              onClick={handleBack}
              title="Back"
            >
              <FiArrowLeft />
            </button>

            <div className="industry-page-heading">
              <h2>{isEditMode ? "Edit Industry" : "Add Industry"}</h2>
              
            </div>
          </div>

          <div className="industry-form-card">
            <form onSubmit={handleSubmit}>
              <div className="industry-form-grid">
                <div className="industry-field">
                  <label htmlFor="code">
                    Code <span className="industry-required">*</span>
                  </label>

                  <input
                    type="text"
                    id="code"
                    name="code"
                    placeholder="OG"
                    value={formData.code}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="industry-field">
                  <label htmlFor="label">
                    Label <span className="industry-required">*</span>
                  </label>

                  <input
                    type="text"
                    id="label"
                    name="label"
                    placeholder="Oil & Gas"
                    value={formData.label}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="industry-field">
                   <label htmlFor="code">
                    Status <span className="industry-required">*</span>
                  </label>
                <button
                  type="button"
                  className={`industry-toggle-switch ${
                    formData.is_active ? "on" : ""
                  }`}
                  onClick={toggleActive}
                  aria-pressed={formData.is_active}
                >
                  <span className="industry-toggle-knob"></span>
                </button>

                <span className="industry-toggle-label">Active</span>
                </div>
              </div>


              <div className="industry-form-actions">
                <button
                  type="button"
                  className="industry-cancel-btn"
                  onClick={handleBack}
                  disabled={loading}
                >
                  <FiX />
                  Cancel
                </button>

                <button
                  type="submit"
                  className="industry-save-btn"
                  disabled={loading}
                >
                  <FiSave />
                  {loading
                    ? "Saving..."
                    : isEditMode
                    ? "Update Industry"
                    : "Save Industry"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddIndustry;